import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import React, { useEffect, useState } from 'react';

interface BaseFieldConfig {
    name: string;
    label: string;
    type: string;
    required?: boolean;
    defaultValue?: any;
    placeholder?: string;
}

interface TextFieldConfig extends BaseFieldConfig {
    type: 'text' | 'email' | 'password' | 'number' | 'url';
}

interface TextareaFieldConfig extends BaseFieldConfig {
    type: 'textarea';
}

interface SelectFieldConfig extends BaseFieldConfig {
    type: 'select';
    options: { value: string; label: string }[];
}

interface CheckboxFieldConfig extends BaseFieldConfig {
    type: 'checkbox';
}

interface SwitchFieldConfig extends BaseFieldConfig {
    type: 'switch';
}

interface CustomFieldConfig extends BaseFieldConfig {
    type: 'custom';
    render: (props: { onChange: (value: any) => void; value?: any }) => React.ReactNode;
}

type FieldConfig = TextFieldConfig | TextareaFieldConfig | SelectFieldConfig | CheckboxFieldConfig | SwitchFieldConfig | CustomFieldConfig;

interface FormDialogProps {
    onClose: () => void;
    data: {
        title: string;
        description?: string;
        fields: FieldConfig[];
        submitText?: string;
    };
    onSave: (data: Record<string, any>) => void;
}

export const FormDialog: React.FC<FormDialogProps> = ({ onClose, data, onSave }) => {
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toast } = useToast();

    // Log when component receives props
    useEffect(() => {
        console.log('FormDialog received props:', { data, onClose: !!onClose, onSave: !!onSave });
    }, [data, onClose, onSave]);

    // Initialize form data with default values
    useEffect(() => {
        if (!data || !data.fields) {
            console.error('FormDialog: data or data.fields is undefined');
            return;
        }

        const initialData: Record<string, any> = {};
        data.fields.forEach(field => {
            if (field.defaultValue !== undefined) {
                initialData[field.name] = field.defaultValue;
            } else {
                // Set default empty values based on field type
                switch (field.type) {
                    case 'checkbox':
                    case 'switch':
                        initialData[field.name] = false;
                        break;
                    case 'number':
                        initialData[field.name] = 0;
                        break;
                    default:
                        initialData[field.name] = '';
                }
            }
        });
        setFormData(initialData);
    }, [data?.fields]);

    const handleChange = (name: string, value: any) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when field is updated
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        if (!data || !data.fields) {
            console.error('FormDialog: data or data.fields is undefined');
            return false;
        }

        const newErrors: Record<string, string> = {};
        data.fields.forEach(field => {
            // Special handling for status field - don't validate it as required
            // since it might be missing from the database
            const isStatusField = field.name === 'status';

            if (field.required && !isStatusField &&
                (formData[field.name] === undefined ||
                    formData[field.name] === null ||
                    formData[field.name] === '')) {
                newErrors[field.name] = `${field.label} is required`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting) {
            return;
        }

        if (!validateForm()) {
            toast({
                title: "Error",
                description: "Please fix the errors in the form",
                variant: "destructive",
            });
            return;
        }

        try {
            setIsSubmitting(true);
            console.log('Submitting form data:', formData);

            // Call onSave with form data
            onSave(formData);

        } catch (error) {
            console.error('Error in form submit:', error);
            toast({
                title: "Error",
                description: "An error occurred while submitting the form",
                variant: "destructive",
            });
            setIsSubmitting(false);
        }
    };

    const renderField = (field: FieldConfig) => {
        const inputClasses = `w-full h-11 text-base bg-muted/30 border-muted-foreground/10 hover:border-muted-foreground/20 focus:border-ring focus:bg-background transition-all rounded-xl ${errors[field.name] ? 'border-destructive' : ''}`;

        switch (field.type) {
            case 'text':
            case 'email':
            case 'password':
            case 'url':
            case 'number':
                return (
                    <div className="grid gap-2.5" key={field.name}>
                        <Label htmlFor={field.name} className="flex gap-1 items-center text-sm font-medium text-foreground/80 pl-1">
                            {field.label}
                            {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        <Input
                            id={field.name}
                            name={field.name}
                            type={field.type}
                            value={formData[field.name] || ''}
                            onChange={(e) => handleChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                            className={inputClasses}
                        />
                        {errors[field.name] && (
                            <p className="text-destructive text-sm pl-1">{errors[field.name]}</p>
                        )}
                    </div>
                );
            case 'textarea':
                return (
                    <div className="grid gap-2.5" key={field.name}>
                        <Label htmlFor={field.name} className="flex gap-1 items-center text-sm font-medium text-foreground/80 pl-1">
                            {field.label}
                            {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        <Textarea
                            id={field.name}
                            name={field.name}
                            value={formData[field.name] || ''}
                            onChange={(e) => handleChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                            className={`min-h-[120px] resize-none text-base leading-relaxed bg-muted/30 border-muted-foreground/10 hover:border-muted-foreground/20 focus:border-ring focus:bg-background transition-all rounded-xl ${errors[field.name] ? 'border-destructive' : ''}`}
                        />
                        {errors[field.name] && (
                            <p className="text-destructive text-sm pl-1">{errors[field.name]}</p>
                        )}
                    </div>
                );
            case 'select':
                return (
                    <div className="grid gap-2.5" key={field.name}>
                        <Label htmlFor={field.name} className="flex gap-1 items-center text-sm font-medium text-foreground/80 pl-1">
                            {field.label}
                            {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        <Select
                            value={formData[field.name] || ''}
                            onValueChange={(value) => handleChange(field.name, value)}
                        >
                            <SelectTrigger id={field.name} className={`h-11 text-base bg-muted/30 border-muted-foreground/10 hover:border-muted-foreground/20 focus:border-ring focus:bg-background transition-all rounded-xl ${errors[field.name] ? 'border-destructive' : ''}`}>
                                <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border-border/60 shadow-xl">
                                {field.options.map(option => (
                                    <SelectItem key={option.value} value={option.value} className="rounded-lg">
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors[field.name] && (
                            <p className="text-destructive text-sm pl-1">{errors[field.name]}</p>
                        )}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="flex items-center space-x-3 p-1" key={field.name}>
                        <Checkbox
                            id={field.name}
                            checked={!!formData[field.name]}
                            onCheckedChange={(checked) =>
                                handleChange(field.name, checked === 'indeterminate' ? false : checked)
                            }
                            className="rounded-md"
                        />
                        <Label htmlFor={field.name} className="flex gap-1 items-center text-sm font-medium text-foreground">
                            {field.label}
                            {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        {errors[field.name] && (
                            <p className="text-destructive text-sm">{errors[field.name]}</p>
                        )}
                    </div>
                );
            case 'switch':
                return (
                    <div className="flex items-center justify-between p-1" key={field.name}>
                        <Label htmlFor={field.name} className="flex gap-1 items-center text-sm font-medium text-foreground">
                            {field.label}
                            {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        <Switch
                            id={field.name}
                            checked={!!formData[field.name]}
                            onCheckedChange={(checked) => handleChange(field.name, checked)}
                        />
                        {errors[field.name] && (
                            <p className="text-destructive text-sm">{errors[field.name]}</p>
                        )}
                    </div>
                );
            case 'custom':
                return (
                    <div className="grid gap-2.5" key={field.name}>
                        <Label htmlFor={field.name} className="flex gap-1 items-center text-sm font-medium text-foreground/80 pl-1">
                            {field.label}
                            {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        {field.render({
                            onChange: (value) => handleChange(field.name, value),
                            value: formData[field.name]
                        })}
                        {errors[field.name] && (
                            <p className="text-destructive text-sm pl-1">{errors[field.name]}</p>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
            <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
                <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">{data?.title || 'Dialog'}</DialogTitle>
                {data?.description && (
                    <DialogDescription className="text-base text-muted-foreground leading-relaxed">
                        {data.description}
                    </DialogDescription>
                )}
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex flex-col">
                <div className="space-y-6 px-8 py-8">
                    {data?.fields ? data.fields.map(renderField) : (
                        <p className="text-destructive">No fields provided</p>
                    )}
                </div>
                <DialogFooter className="p-6 bg-muted/5 border-t border-border/40">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="min-w-[100px] rounded-xl hover:bg-muted/50"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
                    >
                        {isSubmitting ? "Saving..." : (data?.submitText || 'Save')}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
};